import { useQuery } from '@apollo/client';
import { faCloudDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import invariant from 'tiny-invariant';
import { graphql } from '~/apollo/generated/v4';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { Button } from '~/components/ui/button';
import { saveAs } from '~/utils/export';
import { createKmlDocument, createKmlFolder } from '~/utils/kml-v4';

const OUTCROP_GEOREFERENCES_EXPORT = graphql(`
  query OutcropGeoreferencesExport($id: ID!) {
    outcropGet(id: $id) {
      id
      name
      georeferences {
        ...georeferenceParts
      }

      studies {
        id
        name
        georeferences {
          ...georeferenceParts
        }
      }
    }
  }
`);

type Props = {
  outcropId: number;
};

export function OutcropGeoreferencesExport({ outcropId }: Props) {
  const { data, loading } = useQuery(OUTCROP_GEOREFERENCES_EXPORT, {
    variables: { id: outcropId },
  });

  async function handleClick() {
    const outcrop = data?.outcropGet;
    invariant(outcrop, `OutcropId ${outcropId} could not be loaded.`);

    const studyFolders = outcrop.studies.map(study => {
      return createKmlFolder(
        study.name,
        `${study.name} Georeferences`,
        study.georeferences,
      );
    });
    const outcropStudiesFolders = [
      createKmlFolder('Studies', null, [], studyFolders),
    ];

    const outcropFolder = createKmlFolder(
      outcrop.name,
      `${outcrop.name} Georeferences`,
      outcrop.georeferences,
      outcropStudiesFolders,
    );

    const document = await createKmlDocument([outcropFolder]);
    saveAs(
      document,
      `${outcrop.name} georeferences with studies.kml`,
      'application/vnd;charset=utf-8',
    );
  }

  if (loading)
    return (
      <>
        <SpinnerPlaceholder>
          Loading outcrop and study georeferences, this may take a long time...
        </SpinnerPlaceholder>
      </>
    );

  return (
    <Button
      type="button"
      color="primary"
      onClick={handleClick}
      className="gap-1"
    >
      <FontAwesomeIcon icon={faCloudDownload} /> Save KML
    </Button>
  );
}
